import React, { useState } from 'react';
import { confirmDialog } from '../ConfirmDialog';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Crown, AlertCircle, RefreshCw, Search, Shield, ShieldOff } from 'lucide-react';
import apiClient from '../../lib/apiClient';
import './SuperAdminDashboard.css';

const FEATURE_LABELS = {
  ai_classification: 'AI Classification',
  global_classification_write: 'Global Classification Writes',
  manual_reclassification: 'Manual Reclassification',
};

const FEATURE_DESCRIPTIONS = {
  ai_classification: 'Allows triggering LLM-backed classification runs, AI suggestions, and audits. Costs money per call.',
  global_classification_write: 'Allows verifying, accepting AI suggestions, and writing to the shared classification data.',
  manual_reclassification: 'Placeholder — reserved for per-user classification overrides once that storage layer ships.',
};

function SuperAdminDashboard() {
  const queryClient = useQueryClient();
  const [search, setSearch] = useState('');
  const [pendingUpdates, setPendingUpdates] = useState({});

  const { data, isLoading, error, refetch, isFetching } = useQuery({
    queryKey: ['super-admin', 'users'],
    queryFn: () => apiClient.get('/api/auth/admin/super/users'),
  });

  // Optimistic (UI-responsiveness review 2026-08-08): the flag switch flips
  // the instant it's clicked; onMutate patches the users cache with rollback,
  // and the invalidate on settle is the truth-up.
  const patchUsers = async (fn) => {
    await queryClient.cancelQueries({ queryKey: ['super-admin', 'users'] });
    const previous = queryClient.getQueryData(['super-admin', 'users']);
    queryClient.setQueryData(['super-admin', 'users'], (prev) => (
      prev && Array.isArray(prev.users) ? { ...prev, users: prev.users.map(fn) } : prev
    ));
    return { previous };
  };
  const rollbackUsers = (_e, _v, ctx) => {
    if (ctx?.previous) queryClient.setQueryData(['super-admin', 'users'], ctx.previous);
  };

  const flagMutation = useMutation({
    mutationFn: ({ userId, flags }) =>
      apiClient.put(`/api/auth/admin/super/users/${userId}/flags`, { flags }),
    onMutate: ({ userId, flags }) =>
      patchUsers((u) => (u.user_id === userId ? { ...u, flags: { ...(u.flags || {}), ...flags } } : u)),
    onError: rollbackUsers,
    onSettled: (_r, _e, variables) => {
      setPendingUpdates(prev => {
        const next = { ...prev };
        delete next[variables.userId];
        return next;
      });
      queryClient.invalidateQueries({ queryKey: ['super-admin', 'users'] });
    },
  });

  const superAdminMutation = useMutation({
    mutationFn: ({ userId, is_super_admin }) =>
      apiClient.put(`/api/auth/admin/super/users/${userId}/super-admin`, { is_super_admin }),
    onMutate: ({ userId, is_super_admin }) =>
      patchUsers((u) => (u.user_id === userId ? { ...u, is_super_admin } : u)),
    onError: rollbackUsers,
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ['super-admin', 'users'] });
    },
  });

  const handleToggleFlag = (user, featureKey, nextValue) => {
    const flags = { [featureKey]: nextValue };
    setPendingUpdates(prev => ({ ...prev, [user.user_id]: featureKey }));
    flagMutation.mutate({ userId: user.user_id, flags });
  };

  const handleToggleSuperAdmin = async (user) => {
    const next = !user.is_super_admin;
    const verb = next ? 'grant' : 'revoke';
    if (!(await confirmDialog(`Are you sure you want to ${verb} super admin for ${user.email}?`))) return;
    superAdminMutation.mutate({ userId: user.user_id, is_super_admin: next });
  };

  const users = data?.users || [];
  const features = data?.features || Object.keys(FEATURE_LABELS);
  const filteredUsers = search
    ? users.filter(u =>
        u.email?.toLowerCase().includes(search.toLowerCase()) ||
        u.role?.toLowerCase().includes(search.toLowerCase())
      )
    : users;

  return (
    <div className="sa-dashboard">
      <header className="sa-dashboard__header">
        <div className="sa-dashboard__title">
          <Crown size={22} />
          <div>
            <h1>Super Admin Dashboard</h1>
            <p>Grant or revoke cost-sensitive feature flags and super admin status per user.</p>
          </div>
        </div>
        <button
          className="sa-dashboard__refresh"
          onClick={() => refetch()}
          disabled={isFetching}
          title="Refresh user list"
        >
          <RefreshCw size={16} className={isFetching ? 'sa-spin' : ''} />
          <span>Refresh</span>
        </button>
      </header>

      <section className="sa-dashboard__feature-key">
        <h2>Feature Keys</h2>
        <ul>
          {features.map(key => (
            <li key={key}>
              <strong>{FEATURE_LABELS[key] || key}</strong>
              <span>{FEATURE_DESCRIPTIONS[key] || ''}</span>
            </li>
          ))}
        </ul>
      </section>

      <section className="sa-dashboard__controls">
        <div className="sa-dashboard__search">
          <Search size={16} />
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Filter by email or role…"
          />
        </div>
        <div className="sa-dashboard__summary">
          {users.length} users · {users.filter(u => u.is_super_admin).length} super admin{users.filter(u => u.is_super_admin).length === 1 ? '' : 's'}
        </div>
      </section>

      {isLoading && (
        <div className="sa-dashboard__loading">
          <RefreshCw size={18} className="sa-spin" />
          <span>Loading users…</span>
        </div>
      )}

      {error && (
        <div className="sa-dashboard__error">
          <AlertCircle size={18} />
          <div>
            <strong>Failed to load users.</strong>
            <span>{error.message}</span>
          </div>
        </div>
      )}

      {!isLoading && !error && (
        <div className="sa-dashboard__table-wrap">
          <table className="sa-dashboard__table">
            <thead>
              <tr>
                <th>User</th>
                <th>Role</th>
                <th>Super Admin</th>
                {features.map(key => (
                  <th key={key} title={FEATURE_DESCRIPTIONS[key]}>
                    {FEATURE_LABELS[key] || key}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {filteredUsers.map(user => {
                const pendingKey = pendingUpdates[user.user_id];
                return (
                  <tr key={user.user_id} className={user.is_super_admin ? 'sa-row--super' : ''}>
                    <td data-label="User">
                      <div className="sa-user-cell">
                        <span className="sa-user-email">{user.email}</span>
                        {user.status !== 'active' && (
                          <span className={`sa-status-badge sa-status-${user.status}`}>{user.status}</span>
                        )}
                      </div>
                    </td>
                    <td data-label="Role">
                      <span className={`sa-role-badge sa-role-${user.role}`}>{user.role}</span>
                    </td>
                    <td data-label="Super Admin">
                      <button
                        type="button"
                        className={`sa-super-toggle ${user.is_super_admin ? 'sa-super-toggle--on' : ''}`}
                        onClick={() => handleToggleSuperAdmin(user)}
                        disabled={superAdminMutation.isPending}
                        title={user.is_super_admin ? 'Revoke super admin' : 'Grant super admin'}
                      >
                        {user.is_super_admin ? <Shield size={14} /> : <ShieldOff size={14} />}
                        <span>{user.is_super_admin ? 'Super' : 'Grant'}</span>
                      </button>
                    </td>
                    {features.map(key => {
                      const effective = user.effective_flags?.[key] === true;
                      const hasOverride = user.overrides?.[key] !== undefined;
                      const disabled =
                        user.is_super_admin ||
                        flagMutation.isPending && pendingKey === key;
                      return (
                        <td key={key} data-label={FEATURE_LABELS[key] || key}>
                          <label className={`sa-toggle ${effective ? 'sa-toggle--on' : ''} ${disabled ? 'sa-toggle--disabled' : ''}`}>
                            <input
                              type="checkbox"
                              checked={effective}
                              disabled={disabled}
                              onChange={(e) => handleToggleFlag(user, key, e.target.checked)}
                            />
                            <span className="sa-toggle__track">
                              <span className="sa-toggle__thumb" />
                            </span>
                            <span className="sa-toggle__label">
                              {user.is_super_admin
                                ? 'Always on'
                                : hasOverride
                                  ? 'Override'
                                  : 'Role default'}
                            </span>
                          </label>
                        </td>
                      );
                    })}
                  </tr>
                );
              })}
              {filteredUsers.length === 0 && (
                <tr>
                  <td colSpan={3 + features.length} className="sa-empty">
                    No users match "{search}".
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      {(flagMutation.error || superAdminMutation.error) && (
        <div className="sa-dashboard__error">
          <AlertCircle size={18} />
          <div>
            <strong>Update failed.</strong>
            <span>{(flagMutation.error || superAdminMutation.error)?.message}</span>
          </div>
        </div>
      )}
    </div>
  );
}

export default SuperAdminDashboard;
